import React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Info } from "lucide-react";
import type { FormikProps } from "formik";

// Import du composant existant
import { AdvancedConditions } from "../AdvancedConditions";

import type { SilaeFormData } from "@/types/silae";

interface Step2ConditionsProps {
  formik: FormikProps<SilaeFormData>;
}

/**
 * Étape 2 : Configuration des conditions d'application
 * Réutilise le composant AdvancedConditions existant
 */
export const Step2Conditions: React.FC<Step2ConditionsProps> = ({ formik }) => {
  const { t } = useTranslation();

  // Déterminer si on affiche les conditions (pour with_conditions et if_else)
  const showConditions = formik.values.structureType !== "simple";
  const hasElseBranch = formik.values.structureType === "if_else";

  // Plus besoin d'adaptateur - on passe directement formik
  return (
    <Card className="space-y-6 p-4 rounded-3xl">
      {/* Configuration des conditions */}
      {showConditions && (
        <>
          <AdvancedConditions formik={formik} />

          {/* Messages d'erreur pour les conditions */}
          {/* <FormikErrorMessage formik={formik} name="conditions" />
          {formik.values.conditions && formik.values.conditions.length > 1 && (
            <FormikErrorMessage formik={formik} name="conditionsOperator" />
          )} */}
        </>
      )}

      {/* Aperçu de la logique */}
      {showConditions && (
        <Card className="bg-background dark:bg-background">
          <CardHeader>
            <CardTitle className="text-lg">
              {t("wizard.conditions.preview.title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Badge variant="default">IF</Badge>
                <span>
                  {t("wizard.conditions.preview.if")}{" "}
                  {formik.values.conditions &&
                  formik.values.conditions.length > 0
                    ? `${formik.values.conditions.length} ${t(
                        "wizard.conditions.preview.conditions",
                      )}`
                    : t("wizard.conditions.preview.noConditions")}{" "}
                </span>
              </div>

              <div className="ml-4 text-muted-foreground">
                → {t("wizard.conditions.preview.mainBonus")}
              </div>

              {hasElseBranch && (
                <>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">ELSE</Badge>
                    <span>{t("wizard.conditions.preview.elseCondition")}</span>
                  </div>
                  <div className="ml-4 text-muted-foreground">
                    → {t("wizard.conditions.preview.alternativeBonus")}
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <Info className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="text-sm text-orange-700 dark:text-orange-300">
                        {t("wizard.conditions.preview.elseInfo")}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </Card>
  );
};
